import { registerSW } from 'virtual:pwa-register';

/** How often an open, visible app looks for a new version. */
const UPDATE_CHECK_MS = 60 * 60 * 1000;

type Listener = (update: () => Promise<void>) => void;
const listeners = new Set<Listener>();
let pendingUpdate: (() => Promise<void>) | null = null;
let registration: ServiceWorkerRegistration | null = null;

/** Subscribe to "new version available"; the callback receives a function that activates it. */
export function onUpdateAvailable(fn: Listener): () => void {
  listeners.add(fn);
  if (pendingUpdate) fn(pendingUpdate);
  return () => listeners.delete(fn);
}

/**
 * Activates a waiting new version (the page reloads once it takes control). Returns false when no
 * update is waiting, so the caller can fall back to a plain reload.
 */
export function applyPendingUpdate(): boolean {
  if (!pendingUpdate) return false;
  void pendingUpdate();
  return true;
}

/**
 * Asks the browser to look for a new service worker now (e.g. after a lazy chunk failed to load,
 * which usually means a deploy happened). A found update is announced through onUpdateAvailable.
 */
export function checkForUpdate(): void {
  void registration?.update().catch(() => undefined);
}

export function startServiceWorker(): void {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  const update = registerSW({
    immediate: true,
    onNeedRefresh() {
      pendingUpdate = () => update(true);
      for (const fn of listeners) fn(pendingUpdate);
    },
    onRegisteredSW(_url, reg) {
      if (!reg) return;
      registration = reg;
      // iOS keeps installed apps alive for a long time: check for updates when the app becomes visible.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void reg.update().catch(() => undefined);
      });
      // A desktop tab or Mac app can stay visible for days without that event; it would keep running
      // the old build, and its lazy chunks (admin) are gone after the deploy.
      setInterval(() => {
        if (document.visibilityState === 'visible' && navigator.onLine) void reg.update().catch(() => undefined);
      }, UPDATE_CHECK_MS);
    },
  });
}
